import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs, Avatar, Button } from 'antd';
import AccentButton from '../components/AccentButton/AccentButton';
import SpecModal from '../components/SpecModal/SpecModal';
import { useHeader } from '../components/Layout/MainLayout';
import SpecActionSheet, { ActionSheetItem } from '../components/SpecActionSheet/SpecActionSheet';
import { PlayButtonLarge } from '../components/PlayButton/PlayButtonLarge';
import { DateSubheader } from '../components/DateSubheader';
import { CalendarDateIcon, LocationMarkerIcon, ChatIcon, CameraIcon, PlusCircleIcon, DefaultAvatar, XIcon } from '../components/icons';
import { BandMember } from '../components/DirectoryListings/ShowListing/ShowListing';
import { Tag, TagRow } from '../components/Tag';
import { PhotoRibbon } from '../components/PhotoGallery/PhotoRibbon';
import { PhotoGrid } from '../components/PhotoGallery/PhotoGrid';
import { PhotoGalleryDrawer } from '../components/PhotoGallery/PhotoGalleryDrawer';
import { AddPhotoDrawer } from '../components/PhotoGallery/AddPhotoDrawer';
import type { PhotoMetadata } from '../components/PhotoGallery/PhotoOverlayInfo';
import { Comment, CommentList, CommentDrawer, AddCommentDrawer } from '../components/Comment';
import { CommentProps } from '../components/Comment/Comment';
import FloatingPrimaryButton from '../components/FloatingPrimaryButton/FloatingPrimaryButton';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import { useShare } from '../hooks/useShare';
import AddToCalendarSheet from '../components/AddToCalendarSheet/AddToCalendarSheet';
import SpecDrawer from '../components/SpecDrawer/SpecDrawer';
import { MusicianListing } from '../components/DirectoryListings/MusicianListing/MusicianListing';
import './Show.css';

const CURRENT_USER = { name: 'Jane Smith', avatarUrl: 'https://i.pravatar.cc/150?img=5' };

// Mock data for the show
const MOCK_SHOW = {
    id: 'show-1',
    bandName: 'Johnny Mullenax Band',
    venueName: 'Mercury Lounge',
    venueAddress: '1747 S Boston Ave, Tulsa, OK 74119',
    date: 'Wed Sep 17',
    time: '12:00 PM',
    dayOfMonth: 17,
    description: 'Live music, tasty brunch, mimosas, and good vibes! Enjoy a relaxing Sunday morning with great food, refreshing drinks, and the best live music in town.',
    postedBy: 'Johnny Mullenax',
    postedByType: 'bandMember' as 'bandMember' | 'showContributor',
    imageUrl: '/images/bands/johnny-mullenax.png',
    genres: ['Psychedelic', 'Bluegrass', 'Rock', 'Funk'],
    bandMembers: [
        { name: 'Johnny Mullenax', avatarUrl: 'https://cdn.prod.website-files.com/632688ceec38a92558da32b0/6670d38dada951f64025c659_johnny-mullenax(jmb).jpg' },
        { name: 'Greg Fallis', avatarUrl: 'https://cdn.prod.website-files.com/632688ceec38a92558da32b0/6670d38b9c4df6cfa82caa64_greg-fallis(jmb).jpg' },
        { name: 'Teddy Scott' },
    ] as BandMember[],
};

// Mock photos
const MOCK_PHOTOS = [
    'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400',
    'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=400',
    'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=400',
    'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=400',
    'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400',
    'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=400',
    'https://images.unsplash.com/photo-1506157786151-b8491531f063?w=400',
    'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=400',
    'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400',
    'https://images.unsplash.com/photo-1498038432885-c6f3f1b912ee?w=400',
    'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400',
    'https://images.unsplash.com/photo-1508854710579-5cecc3a9ff17?w=400',
];

// Mock photo metadata (parallel to MOCK_PHOTOS)
const MOCK_PHOTO_METADATA: PhotoMetadata[] = [
    {
        userName: 'Jane Smith',
        timestamp: 'Sept 16 3:29 PM',
        userTags: ['fan'],
        userProfileUrl: '/fan-profile',
        eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' },
    },
    {
        userName: 'George Smith',
        userAvatarUrl: 'https://i.pravatar.cc/150?img=8',
        timestamp: 'Sept 16 2:10 PM',
        userTags: ['fan', 'showContributor'],
        userProfileUrl: '/fan-profile',
        eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' },
    },
    {
        userName: 'Mike Johnson',
        userAvatarUrl: 'https://i.pravatar.cc/150?img=12',
        timestamp: 'Sept 15 8:45 PM',
        userTags: ['musician'],
        userProfileUrl: '/musician-profile',
        eventInfo: { artistName: 'The Restless Kind', date: 'Sat Sep 13', venue: 'The Vanguard' },
    },
    {
        userName: 'Sarah Wilson',
        timestamp: 'Sept 15 6:30 PM',
        userTags: ['fan'],
        userProfileUrl: '/fan-profile',
        eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Fri Sep 12', venue: 'Cain\'s Ballroom' },
    },
    {
        userName: 'Tom Davis',
        userAvatarUrl: 'https://i.pravatar.cc/150?img=15',
        timestamp: 'Sept 14 2:15 PM',
        userTags: ['showContributor'],
        userProfileUrl: '/fan-profile',
        eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' },
    },
    {
        userName: 'Emily Clark',
        userAvatarUrl: 'https://i.pravatar.cc/150?img=20',
        timestamp: 'Sept 13 9:00 PM',
        userTags: ['fan'],
        userProfileUrl: '/fan-profile',
        eventInfo: { artistName: 'Neon Drifters', date: 'Sat Sep 6', venue: 'The Colony' },
    },
    {
        userName: 'Alex Turner',
        userAvatarUrl: 'https://i.pravatar.cc/150?img=22',
        timestamp: 'Sept 12 7:30 PM',
        userTags: ['musician'],
        userProfileUrl: '/musician-profile',
        eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 10', venue: 'Mercury Lounge' },
    },
    {
        userName: 'Chris Martin',
        timestamp: 'Sept 11 5:00 PM',
        userTags: [],
        eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' },
    },
    {
        userName: 'Jordan Lee',
        userAvatarUrl: 'https://i.pravatar.cc/150?img=30',
        timestamp: 'Sept 10 4:20 PM',
        userTags: ['fan'],
        userProfileUrl: '/fan-profile',
        eventInfo: { artistName: 'The Restless Kind', date: 'Fri Sep 5', venue: 'Soundpony' },
    },
    {
        userName: 'Rachel Kim',
        userAvatarUrl: 'https://i.pravatar.cc/150?img=32',
        timestamp: 'Sept 9 11:00 AM',
        userTags: ['fan', 'showContributor'],
        userProfileUrl: '/fan-profile',
        eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' },
    },
    {
        userName: 'Ben Torres',
        userAvatarUrl: 'https://i.pravatar.cc/150?img=35',
        timestamp: 'Sept 8 8:45 PM',
        userTags: ['musician'],
        userProfileUrl: '/musician-profile',
        eventInfo: { artistName: 'Neon Drifters', date: 'Sat Sep 6', venue: 'The Colony' },
    },
    {
        userName: 'Lisa Nguyen',
        timestamp: 'Sept 7 3:15 PM',
        userTags: ['fan'],
        userProfileUrl: '/fan-profile',
        eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Fri Sep 5', venue: 'Cain\'s Ballroom' },
    },
];

// Mock comments
const MOCK_COMMENTS: Omit<CommentProps, 'className'>[] = [
    {
        userName: 'Jane Smith',
        timestamp: 'Sept 16 3:29 PM',
        userTags: [],
        text: 'three',
    },
    {
        userName: 'George Smith',
        avatarUrl: 'https://i.pravatar.cc/150?img=8',
        timestamp: 'Sept 16 3:29 PM',
        userTags: ['fan', 'showContributor'],
        text: 'TEAM DRINK! Welcome back from the tour boys, looking forward to a vegas bomb or three',
    },
    {
        userName: 'Mike Johnson',
        avatarUrl: 'https://i.pravatar.cc/150?img=12',
        timestamp: 'Sept 15 8:45 PM',
        userTags: ['musician'],
        text: 'Can\'t wait to see you guys play! The last show was incredible.',
    },
    {
        userName: 'Sarah Wilson',
        timestamp: 'Sept 15 6:30 PM',
        userTags: ['fan'],
        text: 'Is there a cover charge for this show?',
    },
    {
        userName: 'Tom Davis',
        avatarUrl: 'https://i.pravatar.cc/150?img=15',
        timestamp: 'Sept 14 2:15 PM',
        userTags: ['showContributor'],
        text: 'Great venue choice! The sound system there is amazing.',
    },
];

const Show: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const showCreated = (location.state as any)?.showCreated === true;
    const [toastMessage, setToastMessage] = useState<string | null>(showCreated ? 'Show Added' : null);
    const share = useShare(setToastMessage);

    useEffect(() => {
        if (showCreated) {
            window.history.replaceState({}, '');
        }
    }, [showCreated]);

    const [actionSheetOpen, setActionSheetOpen] = useState(false);
    const [deleteShowModalOpen, setDeleteShowModalOpen] = useState(false);
    const [calendarSheetOpen, setCalendarSheetOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('comments');
    const [photoDrawerOpen, setPhotoDrawerOpen] = useState(false);
    const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
    const [addCommentDrawerOpen, setAddCommentDrawerOpen] = useState(false);
    const [initialCommentText, setInitialCommentText] = useState('');
    const [addPhotoDrawerOpen, setAddPhotoDrawerOpen] = useState(false);
    const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
    const [pendingPhotoPreview, setPendingPhotoPreview] = useState<string | null>(null);
    const photoFileInputRef = useRef<HTMLInputElement>(null);
    const [comments, setComments] = useState(MOCK_COMMENTS);
    const [photos, setPhotos] = useState(MOCK_PHOTOS);
    const [photoMeta, setPhotoMeta] = useState(MOCK_PHOTO_METADATA);
    const [highlightedCommentIndex, setHighlightedCommentIndex] = useState<number | null>(null);
    const [highlightedPhotoIndex, setHighlightedPhotoIndex] = useState<number | null>(null);
    const [bandMembersDrawerOpen, setBandMembersDrawerOpen] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const { setOnMoreClick, setRightExtra, isAuthenticated } = useHeader();
    const commentListRef = useRef<HTMLDivElement>(null);
    const photoGridRef = useRef<HTMLDivElement>(null);

    const showContext = {
        bandName: MOCK_SHOW.bandName,
        venueName: MOCK_SHOW.venueName,
        date: MOCK_SHOW.date,
        imageUrl: MOCK_SHOW.imageUrl,
    };

    const triggerAddPhoto = useCallback(() => {
        photoFileInputRef.current?.click();
    }, []);

    const handlePhotoFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setPendingPhotoFile(file);
        setPendingPhotoPreview(URL.createObjectURL(file));
        setAddPhotoDrawerOpen(true);
        e.target.value = '';
    }, []);

    const handleAddPhotoClose = useCallback(() => {
        setAddPhotoDrawerOpen(false);
        setPendingPhotoFile(null);
        if (pendingPhotoPreview) {
            URL.revokeObjectURL(pendingPhotoPreview);
        }
        setPendingPhotoPreview(null);
    }, [pendingPhotoPreview]);

    const openAddComment = useCallback((prefill = '') => {
        setInitialCommentText(prefill);
        setAddCommentDrawerOpen(true);
    }, []);

    const handleEmojiClick = useCallback((emoji: string) => {
        setCommentDrawerOpen(false);
        openAddComment(emoji);
    }, [openAddComment]);

    const handleCommentSubmit = useCallback((data: { text: string; userName?: string }) => {
        const newComment: Omit<CommentProps, 'className'> = {
            userName: data.userName || CURRENT_USER.name,
            avatarUrl: isAuthenticated ? CURRENT_USER.avatarUrl : undefined,
            timestamp: 'Just now',
            userTags: isAuthenticated ? ['fan'] : [],
            text: data.text,
        };
        setComments(prev => [newComment, ...prev]);
        setAddCommentDrawerOpen(false);
        setActiveTab('comments');
        setHighlightedCommentIndex(0);
        setToastMessage('Comment Added');
        setTimeout(() => {
            const firstComment = commentListRef.current?.querySelector('.spec-comment');
            firstComment?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        setTimeout(() => setHighlightedCommentIndex(null), 3000);
    }, []);

    const handlePhotoSubmit = useCallback((data: { file: File; previewUrl: string; userName?: string }) => {
        setPhotos(prev => [data.previewUrl, ...prev]);
        const newMeta: PhotoMetadata = {
            userName: data.userName || CURRENT_USER.name,
            userAvatarUrl: isAuthenticated ? CURRENT_USER.avatarUrl : undefined,
            timestamp: 'Just now',
            userTags: isAuthenticated ? ['fan'] : [],
            userProfileUrl: isAuthenticated ? '/fan-profile' : undefined,
            eventInfo: {
                artistName: MOCK_SHOW.bandName,
                artistAvatarUrl: MOCK_SHOW.imageUrl,
                date: MOCK_SHOW.date,
                venue: MOCK_SHOW.venueName,
            },
        };
        setPhotoMeta(prev => [newMeta, ...prev]);
        setAddPhotoDrawerOpen(false);
        setPendingPhotoFile(null);
        setPendingPhotoPreview(null);
        setActiveTab('photos');
        setHighlightedPhotoIndex(0);
        setToastMessage('Photo Added');
        setTimeout(() => {
            const firstPhoto = photoGridRef.current?.querySelector('.photo-grid-item');
            firstPhoto?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        setTimeout(() => setHighlightedPhotoIndex(null), 3000);
    }, []);

    // Register the More button callback and Add to Calendar header button
    useEffect(() => {
        setOnMoreClick(() => setActionSheetOpen(true));
        setRightExtra(
            <AccentButton
                size="small"
                shape="round"
                onClick={() => setCalendarSheetOpen(true)}
            >
                Add to Calendar
            </AccentButton>
        );
        
        // Cleanup on unmount
        return () => {
            setOnMoreClick(undefined);
            setRightExtra(undefined);
        };
    }, [setOnMoreClick, setRightExtra]);

    // Simulate audio progress when playing
    useEffect(() => {
        if (!isPlaying) return;
        
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    setIsPlaying(false);
                    return 0;
                }
                return prev + 1;
            });
        }, 300);

        return () => clearInterval(interval);
    }, [isPlaying]);

    const handlePlayToggle = () => {
        setIsPlaying(!isPlaying);
    };


    const actionSheetItems: ActionSheetItem[] = [
        {
            key: 'share',
            label: 'Share',
            onClick: () => share({ title: `${MOCK_SHOW.bandName} at ${MOCK_SHOW.venueName}`, text: MOCK_SHOW.description }),
        },
        {
            key: 'copy-link',
            label: 'Copy Link',
            onClick: () => { navigator.clipboard?.writeText(window.location.href).catch(() => {}); setToastMessage('Link Copied'); },
        },
        {
            key: 'add-to-calendar',
            label: 'Add to Calendar',
            onClick: () => setCalendarSheetOpen(true),
        },
        {
            key: 'qr-code',
            label: 'QR Code',
            onClick: () => navigate(`/show/${MOCK_SHOW.id}/qr`),
        },
        {
            key: 'edit-show',
            label: 'Edit Show',
            onClick: () => navigate('/add-show', { state: { editMode: true } }),
        },
        {
            key: 'delete',
            label: 'Delete',
            danger: true,
            onClick: () => setDeleteShowModalOpen(true),
        },
    ];

    return (
        <div className="show-page">
            {/* Fixed Blurred Hero — sits behind everything, content scrolls over it */}
            <div className="show-page__hero">
                <div
                    className="show-page__hero-bg"
                    style={{ backgroundImage: `url(${MOCK_SHOW.imageUrl})` }}
                />
                <div className="show-page__hero-overlay" />
            </div>

            {/* Play button — fixed above header so it's always tappable */}
            <div className="show-page__play-button">
                <PlayButtonLarge
                    size={52}
                    isPlaying={isPlaying}
                    progress={progress}
                    onClick={handlePlayToggle}
                />
            </div>

            {/* Transparent spacer — reserves space so hero is visible initially */}
            <div className="show-page__hero-spacer" />

            {/* Content body — black bg slides over the fixed hero */}
            <div className="show-page__content-body">
                <SpecAlert message={toastMessage} onDone={() => setToastMessage(null)} />

                {/* Sticky Date Subheader */}
                <div className="show-page__date-section">
                    <DateSubheader
                        dateLabel={MOCK_SHOW.date}
                        pillLabel="Today"
                        pillColor="red"
                        showNavArrows={false}
                        dateClickable={false}
                        timeLabel={MOCK_SHOW.time}
                    />
                </div>

                {/* Show Info Section */}
                <div className="show-page__info-section">
                    <div className="show-page__info-header" onClick={() => navigate('/band-profile')}>
                        <div className="show-page__band-avatar">
                            <img
                                className="show-page__band-avatar-img"
                                src={MOCK_SHOW.imageUrl}
                                alt={MOCK_SHOW.bandName}
                            />
                            <CalendarDateIcon
                                day={MOCK_SHOW.dayOfMonth}
                                size={28}
                                className="show-page__band-avatar-calendar"
                            />
                        </div>
                        <div className="show-page__info-text">
                            <h1 className="show-page__band-name">{MOCK_SHOW.bandName}</h1>
                            <div
                                className="show-page__with show-page__with--clickable"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setBandMembersDrawerOpen(true);
                                }}
                            >
                                <Avatar.Group size="small" max={{ count: 3 }}>
                                    {MOCK_SHOW.bandMembers.map((member, index) => (
                                        <Avatar
                                            key={index}
                                            src={member.avatarUrl}
                                            size="small"
                                            icon={!member.avatarUrl ? <DefaultAvatar size={16} /> : undefined}
                                        />
                                    ))}
                                </Avatar.Group>
                                <span className="show-page__with-text">
                                    With {MOCK_SHOW.bandMembers[0]?.name} and more...
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="show-page__venue-row" onClick={() => navigate('/venue-profile')}>
                        <div className="show-page__venue-icon-circle">
                            <LocationMarkerIcon style={{ width: '32px', height: '32px' }} color="rgba(171, 171, 171, 0.8)" />
                        </div>
                        <div className="show-page__venue-info">
                            <span className="show-page__venue-name">{MOCK_SHOW.venueName}</span>
                            <span
                                className="show-page__venue-address show-page__venue-address--clickable"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(
                                        `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(MOCK_SHOW.venueAddress)}`,
                                        '_blank',
                                        'noopener,noreferrer'
                                    );
                                }}
                            >
                                {MOCK_SHOW.venueAddress}
                            </span>
                        </div>
                    </div>

                    <div className="show-page__description-group">
                        <p className="show-page__description">{MOCK_SHOW.description}</p>
                        <p className="show-page__posted-by">
                            <span className="show-page__posted-by-icon">
                                <PlusCircleIcon style={{ color: '#404040' }} />
                            </span>
                            Posted by {MOCK_SHOW.postedBy}
                        </p>
                    </div>
                </div>

                {/* Genre Tags */}
                <div className="show-page__tags-section">
                    <TagRow mode="ribbon">
                        {MOCK_SHOW.genres.map((genre) => (
                            <Tag key={genre}>{genre}</Tag>
                        ))}
                    </TagRow>
                </div>

                {/* Recent Photos Label */}
                <h6 className="show-page__section-label">Past Pics</h6>

                {/* Photo Ribbon */}
                <div className="show-page__ribbon-section">
                    <PhotoRibbon
                        photos={photos}
                        maxPhotos={10}
                        photoMetadata={photoMeta}
                        showOverlayUserInfo
                        showOverlayEventInfo
                    />
                </div>

                {/* Tabs: Comments / Photos */}
                <div className="show-page__tabs-section">
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        centered
                        items={[
                            {
                                key: 'comments',
                                label: (
                                    <span className="show-page__tab-label">
                                        <ChatIcon size="s" tone="inherit" />
                                        Chat
                                    </span>
                                ),
                                children: (
                                    <div className="show-page__tab-content" ref={commentListRef}>
                                        <CommentList
                                            comments={comments}
                                            maxComments={3}
                                            onViewAll={() => setCommentDrawerOpen(true)}
                                            onAddComment={() => openAddComment()}
                                            onEmojiClick={handleEmojiClick}
                                            highlightedIndex={highlightedCommentIndex}
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: 'photos',
                                label: (
                                    <span className="show-page__tab-label">
                                        <CameraIcon size="s" tone="inherit" />
                                        Pics
                                    </span>
                                ),
                                children: (
                                    <div className="show-page__tab-content" ref={photoGridRef}>
                                        <PhotoGrid
                                            photos={photos}
                                            maxPhotos={9}
                                            onViewAll={() => setPhotoDrawerOpen(true)}
                                            onAddPhoto={triggerAddPhoto}
                                            photoMetadata={photoMeta}
                                            showOverlayUserInfo
                                            highlightedIndex={highlightedPhotoIndex}
                                        />
                                    </div>
                                ),
                            },
                        ]}
                    />
                </div>

                {/* Map Section */}
                <div className="show-page__map-container">
                    <img
                        className="show-page__map-image"
                        src="/images/map-dark.png"
                        alt="Venue map"
                    />
                    <div className="show-page__map-gradient" />
                    <div className="show-page__map-content">
                        <svg width="122" height="122" viewBox="0 0 122 122" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g filter="url(#filter0_dd_2092_18034)">
                                <circle cx="61" cy="41" r="36" fill="#222222"/>
                            </g>
                            <circle cx="61" cy="41" r="16.5" fill="#404040" stroke="#131618"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M56.4744 35.5602C57.6747 34.3599 59.3026 33.6855 61.0001 33.6855C62.6975 33.6855 64.3255 34.3599 65.5258 35.5602C66.7261 36.7605 67.4004 38.3884 67.4004 40.0859C67.4004 41.7833 66.7261 43.4113 65.5258 44.6116L61.0001 49.1373L56.4744 44.6116C55.88 44.0173 55.4085 43.3117 55.0868 42.5352C54.7652 41.7587 54.5996 40.9264 54.5996 40.0859C54.5996 39.2454 54.7652 38.4131 55.0868 37.6365C55.4085 36.86 55.88 36.1545 56.4744 35.5602ZM61.0001 41.9144C61.485 41.9144 61.9501 41.7218 62.2931 41.3789C62.636 41.0359 62.8286 40.5708 62.8286 40.0859C62.8286 39.6009 62.636 39.1358 62.2931 38.7929C61.9501 38.45 61.485 38.2573 61.0001 38.2573C60.5151 38.2573 60.05 38.45 59.7071 38.7929C59.3642 39.1358 59.1715 39.6009 59.1715 40.0859C59.1715 40.5708 59.3642 41.0359 59.7071 41.3789C60.05 41.7218 60.5151 41.9144 61.0001 41.9144Z" fill="#ABABAB" fillOpacity="0.8"/>
                            <defs>
                                <filter id="filter0_dd_2092_18034" x="0" y="0" width="122" height="122" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                    <feOffset dy="10"/>
                                    <feGaussianBlur stdDeviation="5"/>
                                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0"/>
                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2092_18034"/>
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                    <feOffset dy="20"/>
                                    <feGaussianBlur stdDeviation="12.5"/>
                                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"/>
                                    <feBlend mode="normal" in2="effect1_dropShadow_2092_18034" result="effect2_dropShadow_2092_18034"/>
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_2092_18034" result="shape"/>
                                </filter>
                            </defs>
                        </svg>
                        <AccentButton size="middle" style={{ marginTop: '-20px' }} onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(MOCK_SHOW.venueAddress)}`, '_blank')}>
                            Get Directions
                        </AccentButton>
                    </div>
                </div>

                {/* About Section */}
                <div className="show-page__about-section">
                    <h2 className="show-page__about-title">About Band</h2>
                    <p className="show-page__about-text">
                        {MOCK_SHOW.bandName} is aksjnsdkn sdkjfn sdkjndskjn sdkjnk jnsd sdkjfn sdkjndskjn sdkjnk jnsd sdkjfn sdkjndskjn sdkjnk jnsd sdkjfn sdkjndskjn sdkjnk
                    </p>
                </div>
            </div>{/* end content-body */}

            {/* Floating Action Buttons — pill + circle, swap per active tab */}
            <div className="show-page__fab-row">
                {/* Left side: Chat action */}
                <FloatingPrimaryButton
                    icon={<ChatIcon size="m" tone="black" />}
                    className={activeTab === 'comments' ? 'show-page__fab-pill' : 'show-page__fab-circle'}
                    style={{ position: 'relative', bottom: 'auto', right: 'auto' }}
                    onClick={() => openAddComment()}
                >
                    {activeTab === 'comments' ? 'Add Comment' : null}
                </FloatingPrimaryButton>

                {/* Right side: Pic action */}
                <FloatingPrimaryButton
                    icon={<CameraIcon size="m" tone="black" />}
                    className={activeTab === 'photos' ? 'show-page__fab-pill' : 'show-page__fab-circle'}
                    style={{ position: 'relative', bottom: 'auto', right: 'auto' }}
                    onClick={triggerAddPhoto}
                >
                    {activeTab === 'photos' ? 'Add Photo' : null}
                </FloatingPrimaryButton>
            </div>

            {/* Photo Gallery Drawer */}
            <PhotoGalleryDrawer
                open={photoDrawerOpen}
                onClose={() => setPhotoDrawerOpen(false)}
                photos={photos}
                title="Show Pics"
                onAddPhoto={() => { setPhotoDrawerOpen(false); triggerAddPhoto(); }}
                fullscreen
                drawerProps={{ zIndex: 1100 }}
                photoMetadata={photoMeta}
                showOverlayUserInfo
            />

            {/* Comment Drawer */}
            <CommentDrawer
                open={commentDrawerOpen}
                onClose={() => setCommentDrawerOpen(false)}
                comments={comments}
                title="Show Chat"
                onAddComment={() => { setCommentDrawerOpen(false); openAddComment(); }}
                onEmojiClick={(emoji) => { setCommentDrawerOpen(false); openAddComment(emoji); }}
                fullscreen
                drawerProps={{ zIndex: 1100 }}
            />

            {/* Add Comment Drawer */}
            <AddCommentDrawer
                open={addCommentDrawerOpen}
                onClose={() => setAddCommentDrawerOpen(false)}
                onSubmit={handleCommentSubmit}
                showContext={showContext}
                isLoggedIn={isAuthenticated}
                currentUser={CURRENT_USER}
                initialText={initialCommentText}
            />

            {/* Add Photo Drawer */}
            <input
                ref={photoFileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoFileSelected}
                style={{ display: 'none' }}
            />
            <AddPhotoDrawer
                open={addPhotoDrawerOpen}
                onClose={handleAddPhotoClose}
                onSubmit={handlePhotoSubmit}
                showContext={showContext}
                isLoggedIn={isAuthenticated}
                currentUser={CURRENT_USER}
                selectedFile={pendingPhotoFile}
                previewUrl={pendingPhotoPreview}
            />

            {/* Action Sheet */}
            <SpecActionSheet
                open={actionSheetOpen}
                onClose={() => setActionSheetOpen(false)}
                items={actionSheetItems}
            />

            {/* Add to Calendar Sheet */}
            <AddToCalendarSheet
                open={calendarSheetOpen}
                onClose={() => setCalendarSheetOpen(false)}
                event={{
                    title: `${MOCK_SHOW.bandName} at ${MOCK_SHOW.venueName}`,
                    description: MOCK_SHOW.description,
                    location: MOCK_SHOW.venueAddress,
                    startDate: '2025-09-17T12:00:00',
                    endDate: '2025-09-17T14:00:00',
                }}
            />

            {/* Delete Show Confirmation Modal */}
            <SpecModal
                open={deleteShowModalOpen}
                onCancel={() => setDeleteShowModalOpen(false)}
                title="Delete Show"
                footer={null}
                closable={false}
                centered
                width={340}
            >
                <p className="show-page__modal-body">
                    Are you sure you want to delete this show? This cannot be undone.
                </p>
                <div className="show-page__modal-actions">
                    <Button
                        size="large"
                        shape="round"
                        onClick={() => setDeleteShowModalOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="large"
                        shape="round"
                        danger
                        type="primary"
                        onClick={() => {
                            console.log('Show deleted');
                            navigate('/home');
                        }}
                    >
                        Delete
                    </Button>
                </div>
            </SpecModal>

            {/* Band Members Drawer */}
            <SpecDrawer
                title={
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <div style={{ width: 40, height: 4, background: 'rgba(255,255,255,0.3)', borderRadius: 2, margin: '0 auto 24px', flexShrink: 0 }} />
                        Band Members
                    </div>
                }
                open={bandMembersDrawerOpen}
                onClose={() => setBandMembersDrawerOpen(false)}
                size="default"
                closable={false}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
                    {MOCK_SHOW.bandMembers.map((member, index) => (
                        <MusicianListing
                            key={index}
                            name={member.name}
                            imageUrl={member.avatarUrl}
                            onClick={() => navigate('/musician-profile')}
                        />
                    ))}
                </div>
            </SpecDrawer>
        </div>
    );
};

export default Show;
